import type { SlugMapService } from '.';
import { effect, signal } from '@preact/signals-react';
import { injectable, inject } from 'src/features/ioc';
import type { SlugMapDict } from 'src/lib/dictionary/dictionary';
import { getWholeSlugMapQuery } from 'src/queries/slugMap';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class SlugMapServiceImpl implements SlugMapService {
    #logger: ReactBindings['Logger'];

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        this.#logger = logger.getSubLogger({ name: 'SlugMapService' });
        const query = queryObserverModelFactory(getWholeSlugMapQuery());

        query.subscribe((result) => {
            if (TRACE) {
                this.#logger.trace('Query result received', {
                    isSuccess: result.isSuccess,
                    hasData: !!result.data,
                    status: result.status,
                });
            }
            if (result.isSuccess) {
                this.slugMap.value = result.data;
                if (TRACE) {
                    this.#logger.trace('slugMap.value set');
                }
            }
        });
    }

    async initialize(): Promise<void> {
        if (TRACE) {
            this.#logger.trace('initialize() called', { hasValue: !!this.slugMap.value });
        }
        if (this.slugMap.value) {
            return;
        }

        if (TRACE) {
            this.#logger.trace('Waiting for slugMap value...');
        }

        using stack = new DisposableStack();

        await new Promise<void>((resolve) => {
            stack.defer(
                effect(() => {
                    if (TRACE) {
                        this.#logger.trace('Effect running', { hasValue: !!this.slugMap.value });
                    }
                    if (this.slugMap.value) {
                        if (TRACE) {
                            this.#logger.trace('Resolving - slugMap loaded');
                        }
                        resolve();
                    }
                }),
            );
        });

        if (TRACE) {
            this.#logger.trace('initialize() complete');
        }
    }

    slugMap = signal<SlugMapDict>(null as any);
}
